import { useState } from "react";
import { APP_NAME } from "@/lib/branding";
import { User, LogOut, UserCircle, ChevronDown, Download, Upload, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useProfileModal } from "@/contexts/ProfileModalContext";
import { ExportModal } from "@/components/forms/ExportModal";
import { ImportTransactions } from "@/components/transactions/ImportTransactions";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface UserSectionProps {
  isCollapsed?: boolean;
}

export function UserSection({ isCollapsed = false }: UserSectionProps) {
  const { user, signOut } = useAuth();
  const { openModal } = useProfileModal();
  const [isOpen, setIsOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await signOut();
      toast.success("Você foi desconectado com sucesso.");
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      toast.error("Erro ao fazer logout. Tente novamente.");
    }
  };

  const handleProfile = () => {
    setIsOpen(false);
    openModal();
  };

  const handleExport = () => {
    setIsOpen(false);
    setExportModalOpen(true);
  };

  const handleImport = () => {
    setIsOpen(false);
    setImportModalOpen(true);
  };

  if (!user) return null;

  if (isCollapsed) {
    return (
      <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="w-10 h-10 rounded-full bg-primary/10 hover:bg-primary/20 transition-colors border border-primary/20 hover:border-primary/30"
            title={user.email}
          >
            <User className="h-5 w-5 text-primary" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          side="right"
          className="w-48 bg-sidebar border border-sidebar-border rounded-lg shadow-lg"
          sideOffset={8}
        >
          <div className="px-3 py-2 border-b border-border">
            <p className="text-xs text-muted-foreground truncate">
              {user.email}
            </p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              {APP_NAME}
            </p>
          </div>
          <DropdownMenuItem 
            onClick={handleProfile}
            className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-accent rounded-md transition-colors"
          >
            <UserCircle className="h-4 w-4" />
            Perfil
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={handleExport}
            className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-accent rounded-md transition-colors"
          >
            <Download className="h-4 w-4" />
            Exportar Dados
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={handleImport}
            className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-accent rounded-md transition-colors"
          >
            <Upload className="h-4 w-4" />
            Importar Dados
          </DropdownMenuItem>
          <DropdownMenuSeparator className="my-1 bg-border" />
          <DropdownMenuItem 
            onClick={handleLogout}
            className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-destructive hover:text-destructive-foreground rounded-md transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Sair
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ExportModal 
        open={exportModalOpen} 
        onOpenChange={setExportModalOpen} 
      />
      <Dialog open={importModalOpen} onOpenChange={setImportModalOpen}>
         <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
           <ImportTransactions onImportSuccess={() => setImportModalOpen(false)} />
         </DialogContent>
       </Dialog>
    </>
  );
  }

  return (
    <>
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="w-full justify-between p-3 h-auto rounded-lg hover:bg-accent/50 hover:text-accent-foreground hover:shadow-sm transition-colors border-0 hover:border-0"
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-9 h-9 rounded-full bg-sidebar-primary flex items-center justify-center flex-shrink-0 shadow-sm">
              <User className="h-4 w-4 text-sidebar-primary-foreground" />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-sm font-medium text-sidebar-foreground truncate leading-tight">
                {user.email?.split('@')[0] || 'Usuário'}
              </p>
              <p className="text-xs text-sidebar-foreground/70 mt-0.5">
                Clique para opções
              </p>
            </div>
          </div>
          <ChevronDown className="h-4 w-4 text-sidebar-foreground/70 flex-shrink-0 ml-2" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-48 bg-sidebar border border-sidebar-border rounded-lg shadow-lg"
        sideOffset={8}
      >
        <DropdownMenuItem 
          onClick={handleProfile}
          className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-accent rounded-md transition-colors"
        >
          <UserCircle className="h-4 w-4" />
          Perfil
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={handleExport}
          className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-accent rounded-md transition-colors"
        >
          <Download className="h-4 w-4" />
          Exportar Dados
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={handleImport}
          className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-accent rounded-md transition-colors"
        >
          <Upload className="h-4 w-4" />
          Importar Dados
        </DropdownMenuItem>
        <DropdownMenuSeparator className="my-1 bg-border" />
        <DropdownMenuItem 
          onClick={handleLogout}
          className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-destructive hover:text-destructive-foreground rounded-md transition-colors"
        >
          <LogOut className="h-4 w-4" />
          Sair
        </DropdownMenuItem>
 </DropdownMenuContent>
      </DropdownMenu>
      <ExportModal 
        open={exportModalOpen} 
        onOpenChange={setExportModalOpen} 
      />
      <Dialog open={importModalOpen} onOpenChange={setImportModalOpen}>
         <DialogContent className="max-w-[90vw] sm:max-w-4xl lg:max-w-5xl max-h-[90vh] overflow-y-auto">
           <DialogHeader className="sr-only">
             <DialogTitle>Importar Transações</DialogTitle>
           </DialogHeader>
           <ImportTransactions onImportSuccess={() => setImportModalOpen(false)} />
         </DialogContent>
       </Dialog>
    </>
  );
}
